import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Breadcrumbs, Link as MuiLink, Divider, Stack, Button,
} from '@mui/material';
import {
  ArrowBack, Description, Handshake, AssignmentReturn, VerifiedUser, Gavel, ContactSupport,
} from '@mui/icons-material';
import { C } from './vps/theme';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: C.bgSection, minHeight: '100vh', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Navigation / Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{
              color: C.primary,
              fontWeight: 600,
              mb: 2,
              '&:hover': { backgroundColor: C.primaryLight },
            }}
          >
            Back to Home
          </Button>

          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
              Home
            </MuiLink>
            <Typography color="text.primary" sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.primary }}>
              Terms & Conditions
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              color: C.textPrimary,
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Terms & Conditions
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 720 }}>
            Last updated: January 2026. Please read these terms carefully before deploying a Virtual Private Server (VPS) or purchasing hosting services with Hosting Services.
          </Typography>
        </Box>

        {/* Content Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5, md: 6 },
            backgroundColor: '#FFFFFF',
            border: `1px solid ${C.border}`,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          }}
        >
          <Stack spacing={4}>
            {/* Section 1 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Description sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  1. Service Overview & Agreement
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                Hosting Services provides self-managed and managed Virtual Private Servers (VPS), cloud hosting, and domain registration. By ordering any hosting plan or initiating a payment via <strong>Cashfree.com Payment Gateway</strong>, you agree to comply with all terms set forth in this agreement.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 2 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Handshake sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  2. User Responsibilities & Server Management
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 1.5 }}>
                For Self-Managed VPS hosting plans, the customer receives full SSH root access and is responsible for managing server operating systems, software installations, and security patches:
              </Typography>
              <Typography component="div" sx={{ color: C.textSecondary, lineHeight: 1.75, pl: 2 }}>
                • Customers must not use VPS servers for illegal activities, unsolicited spamming, or hosting malware.<br />
                • You are responsible for safeguarding your root passwords and API access keys.<br />
                • Server resource usage must comply with allocated vCPU, RAM, and NVMe SSD limits.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 3 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <AssignmentReturn sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  3. Pricing, Taxes & Cashfree Payments
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                All prices listed on Hosting Services are in Indian Rupees (INR) and are subject to 18% Government GST where applicable. Payments are processed securely via <strong>Cashfree.com Payment Gateway</strong>. Subscriptions renew automatically unless cancelled prior to the billing date.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 4 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <VerifiedUser sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  4. 30-Day Money-Back Guarantee & Refund Policy
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                We stand behind our infrastructure. All new VPS hosting plans include a <strong>30-Day Money-Back Guarantee</strong>. If you are unsatisfied with server performance or uptime within the first 30 days of purchase, contact support for a full refund processed back to your original Cashfree payment source.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 5 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Gavel sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  5. Uptime & SLA Guarantee
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                Hosting Services guarantees 99.99% network and server uptime across all VPS node clusters. Scheduled maintenance is announced in advance via account notifications.
              </Typography>
            </Box>

            {/* <Divider sx={{ borderColor: C.border }} /> */}

            {/* Section 6 */}
            {/* <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <ContactSupport sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  6. Contact Support
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 2 }}>
                For billing inquiries, SLA requests, or account questions regarding these Terms & Conditions, please contact us:
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: C.bgSection,
                  borderRadius: '12px',
                  border: `1px solid ${C.border}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                  Hosting Services Support Team
                </Typography>
                <Typography variant="body2" sx={{ color: C.textSecondary, mt: 0.5 }}>
                  Email: support@hostingservices.com | Available 24/7/365
                </Typography>
              </Box>
            </Box> */}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;
